import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Video } from 'lucide-react'
import type { MeetingLanguage } from '@hub/contracts'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { useLocale } from '@/i18n/locale'
import { MeetingLanguageToggle } from './meeting-language-toggle'

/**
 * "Starta möte" asks one question before it starts anything (board m5gc #1):
 * which language the meeting is in. It defaults to the starter's profile
 * locale, and the answer is pinned on the meeting — Whisper's language hint,
 * the live summary and the final notes all follow it.
 */
export function StartMeetingDialog({
  open,
  onOpenChange,
  starting,
  error,
  onStart,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  starting: boolean
  error: string | null
  onStart: (language: MeetingLanguage) => void
}) {
  const { t } = useTranslation('meetings')
  const { locale } = useLocale()
  const [language, setLanguage] = useState<MeetingLanguage>(locale === 'en' ? 'en' : 'sv')

  useEffect(() => {
    if (open) setLanguage(locale === 'en' ? 'en' : 'sv')
  }, [open, locale])

  return (
    <Dialog open={open} onOpenChange={(next) => !starting && onOpenChange(next)}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>{t('hub.startTitle')}</DialogTitle>
          <DialogDescription>{t('hub.startDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label>{t('create.language')}</Label>
          <MeetingLanguageToggle value={language} onChange={setLanguage} disabled={starting} />
        </div>

        {error && <p className="text-destructive text-sm">{error}</p>}

        <DialogFooter>
          <Button variant="outline" disabled={starting} onClick={() => onOpenChange(false)}>
            {t('create.cancel')}
          </Button>
          <Button disabled={starting} onClick={() => onStart(language)}>
            {starting ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Video className="h-3.5 w-3.5" />
            )}
            {starting ? t('hub.starting') : t('hub.start')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
